import { inject, observer } from 'mobx-react';
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { I18n } from '@byted-cg/linkchat-plugin';

import { AppContextType, DIALOG_TYPE } from '@byted-cg/linkchat-plugin';
import { DialogInput } from '@byted-cg/linkchat-ui';
import { IInputRef } from '@byted-cg/linkchat-ui/es/components/dialog-input';
import keyboardpng from '../../../../public/keyboard.png';
import videopng from '../../../../public/video.png';

import { Icon } from '../components/Icon';
import classes from '../constants/classes';
import { logger } from '../constants/logger';
import { SEND_ORIGIN } from '../constants/message';
import { isLark } from '../utils';
import canUse, { canUseEnterKeyHint } from '../utils/canUse';
import { prefixCls } from '../utils/style';
import AutoComplete from './AutoComplete';
import { usePanel } from './hooks/usePanel';
import AudioWrapper from './AudioWrapper';
import PasteFile, { NewFile } from './PasteFile';

interface InputWrapperProps {
  appContext?: AppContextType;
  onChange?: (query: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSend?: (value?: string) => void;
  rightButtonList?: React.ReactElement;
  children?: React.ReactElement;
  showRecord?: Boolean;
}

export type IInputWrapperRef = {
  replace: (value: string) => void;
  getValue: () => string;
  getEle: () => HTMLTextAreaElement;
  focus: () => void;
  blur: () => void;
  input: (str: string) => void;
  isFocus: () => boolean | undefined;
};

const BaseDialogImgStyle = {
  maxWidth: '100%',
  margin: '0 auto',
  display: 'block',
  maxHeight: '44vh',
};

// 输入最后一个单词是韩文需要特殊处理下，点击发送的时候把键盘收起来，否则会因为没有清空联想词，导致下次输入带进去
const lastWordKoeraInput = (val: string) => {
  try {
    const lastWord = val.charAt(val.length - 1);
    const isKoreaInput =
      /[\uac00-\ud7af]|[\u1100-\u11ff]|[\u3130-\u318f]|[\ua960-\ua97f]|[\ud7b0-\ud7ff]/g.test(
        lastWord,
      );
    return isKoreaInput;
  } catch (error) {
    console.log('error', error);
  }
  return false;
};

const InputWrapper = React.forwardRef<IInputWrapperRef, InputWrapperProps>(
  (
    {
      appContext,
      onChange,
      onFocus,
      onBlur,
      onSend,
      rightButtonList,
      children,
    },
    ref,
  ) => {
    const [currentPanel, _setCurrentPanel] = usePanel();
    const { query = {} } = appContext || {};
    const [sendValue, setSendValue] = useState('');
    const inputRef = useRef<IInputRef>(null);
    /** input 提交状态 */
    const inputSubmittingRef = useRef<boolean>(false);

    const canEnterKeyHint = useMemo(() => canUseEnterKeyHint(), []);

    const [showRecord, setShowRecord] = useState(false);

    const [pasteFiles, setPasteFiles] = useState<NewFile[]>([]);

    const callRecord = useCallback(
      (e: any) => {
        e.stopPropagation();
        setShowRecord(!showRecord);
      },
      [showRecord, setShowRecord],
    );

    const onInputChange = useCallback(
      (value: string) => {
        setSendValue(value);
        onChange && onChange(value);
      },
      [sendValue],
    );
    const onHandleInput = useCallback(
      (e: any) => {
        const valueFromEvent = e.target.value;
        // 兼容输入表情的case下，如果使用了全选删除，onchange不生效的case
        if (!valueFromEvent && sendValue) {
          onInputChange('');
        }
      },
      [sendValue],
    );

    const sendHandle = useCallback(async () => {
      if (!sendValue) return;
      onSend && onSend(sendValue);
      onInputChange('');
      if (!currentPanel || currentPanel === 'keyboard') {
        inputRef.current?.focus();
      }
      try {
        appContext?.sendMessage &&
          (await appContext?.sendMessage(
            {
              content: sendValue,
              origin: SEND_ORIGIN.INPUT,
            },
            // 会话细分，需要设置用户输入的messageType为-1 @宇森
            {
              messageType: '-1',
            },
          ));
      } catch (error) {
        appContext?.logger?.error(logger.inputWrapper, {
          error,
        });
        throw error;
      }
      if (lastWordKoeraInput(sendValue)) {
        inputRef.current?.blur();
      }
    }, [onInputChange, onSend]);

    useImperativeHandle(ref, (): IInputWrapperRef => {
      return {
        replace: (value: string) => onInputChange(value),
        getValue: () => sendValue,
        getEle: () => inputRef.current?.getEle() as HTMLTextAreaElement,
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        input: (str: string) => inputRef.current?.input(str),
        isFocus: () => inputRef.current?.isFocus(),
      };
    });

    // Monitor changes in the number of lines in the text box
    const handleRowChange = () => {
      appContext?.$refs?.$scroller?.current?.scrollToBottom(false);
    };

    const onKeyDown = useCallback(
      (e: any) => {
        if (13 === e.keyCode && (e.shiftKey || e.ctrlKey)) {
          return;
        }
        if (!canUse('touch')) {
          if (e.keyCode === 13 && inputRef.current) {
            sendHandle();
            e.preventDefault();
          }
        } else {
          if (canEnterKeyHint && e.keyCode === 13 && inputRef.current) {
            sendHandle();
            e.preventDefault();
          }
        }
      },
      [sendHandle],
    );

    const onFastSend = useCallback(
      async (query?: string) => {
        try {
          if (query) {
            appContext?.sendMessage &&
              (await appContext?.sendMessage({
                content: query,
              }));
          }
          setSendValue('');
        } catch (error) {}
      },
      [appContext?.sendMessage],
    );
    const onPaste = useCallback(
      async (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (
          e.target === inputRef.current?.getEle() &&
          !inputSubmittingRef.current
        ) {
          const data = e.clipboardData || (window as any).clipboardData;
          //Get image content
          const files = data.files;
          if (files.length) {
            e.preventDefault();
            setPasteFiles([...files]);
          }
        }
      },
      [query?.isPc],
    );

    const onHandlerFile = useCallback(() => {
      setPasteFiles([]);
    }, []);
    return (
      <>
        {appContext?.renderConfig?.openAudio &&
          appContext?.dialog?.ext?.type === DIALOG_TYPE.AGENT && (
            <div className="recorder-container" onClick={callRecord}>
              <img
                className="recorder"
                src={showRecord ? keyboardpng : videopng}
              ></img>
            </div>
          )}
        {showRecord ? (
          <AudioWrapper appContext={appContext}></AudioWrapper>
        ) : (
          <div
            className={prefixCls(
              {
                [`${classes.prefix}InputWrapper`]: true,
              },
              appContext?.query?.isPc,
            )}
          >
            <AutoComplete onChange={onFastSend} keywords={sendValue} />
            <div
              className={prefixCls(
                {
                  [`${classes.prefix}Input`]: true,
                },
                appContext?.query?.isPc,
              )}
              onPaste={onPaste}
            >
              <DialogInput
                onBlur={onBlur}
                onFocus={onFocus}
                onInput={onHandleInput}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                value={sendValue}
                ref={inputRef}
                onRowChange={handleRowChange}
                autoSize
                placeholder={
                  isLark()
                    ? I18n.t(
                        'enter_content_or_paste_after_screenshot_using_feishu_shortcut',
                        {},
                        '输入内容或使用飞书快捷键截图后粘贴',
                      )
                    : I18n.t('input_placeholder', {}, '请输入您想咨询的问题~')
                }
              />
              {children}
            </div>
          </div>
        )}
        {rightButtonList}
        <div
          className="buttonGroup buttonGroup__right"
          style={{
            display: sendValue?.length ? 'flex' : 'none',
          }}
        >
          <Icon
            type="Send"
            className={prefixCls(
              {
                SendMessageButton: true,
              },
              appContext?.query?.isPc,
            )}
            onClick={sendHandle}
          />
        </div>
        {
          <PasteFile
            appContext={appContext}
            files={pasteFiles}
            show={Boolean(pasteFiles.length)}
            onHandler={onHandlerFile}
          ></PasteFile>
        }
      </>
    );
  },
);

export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(InputWrapper));
